import { useId } from 'react';
import styled, { css } from 'styled-components';
import { Spacing4, Spacing12, Spacing16, Spacing32 } from 'autogen/design-tokens/tokens';
import { Label, Typography } from 'components/generic/typography';
import type { LabelProps } from 'components/generic/typography/components/Label';
import Flex from 'components/Layout/components/Flex';
import format from 'helpers/translations';
import HelperText from '../../components/HelperText';
import { SelectButtonStyling } from '../shared/styles';
import type ISelect from '../shared/types/select';
import type { SelectSizes } from '../shared/types/select';

/* #region  styles */
const NativeDropdown = styled(Label)<{ sizeProp?: SelectSizes; $isInvalid?: boolean; $strong: LabelProps['strong'] }>`
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url(/images/icons_arrow.svg);
    background-size: 14px;
    background-repeat: no-repeat;
    background-position: right ${Spacing16} center;
    
    ${({ sizeProp }) => {
        switch (sizeProp) {
            case 'lg':
                return css`
                    height: 48px;
                    padding: 0 ${Spacing32} 0 ${Spacing16};
                `;
            case 'md':
                return css`
                    height: 40px;
                    padding: 0 ${Spacing32} 0 ${Spacing12};
                `;
            default:
                return css`
                    height: 40px;
                    padding: 0 ${Spacing32} 0 ${Spacing12};
                `;
        }
    }}

    ${SelectButtonStyling}
    
`;

const LabelContainer = styled.label`
       display: inline-flex;
       flex-direction: column;
       gap: ${Spacing4};
       margin-bottom: 0;
       min-width: 0;
`;
/* #endregion */

export default function NativeSelect({
    value,
    onChange,
    options,
    placeholderText,
    size,
    label,
    isInvalid,
    helperText,
    ...rest
}: ISelect) {
    const helperTextId = useId();

    const handleChange = (e) => {
        const selected = options.find((option) => option.value === e.target.value);

        if (selected) {
            onChange(selected);
        }
    };

    const baseSelect = (
        <NativeDropdown
            forwardedAs="select"
            $strong={false}
            $isInvalid={isInvalid}
            value={value?.value}
            onChange={handleChange}
            sizeProp={size}
            aria-describedby={helperTextId}
            {...rest}
        >
            {placeholderText && (
                <option hidden disabled>
                    {format(placeholderText)}
                </option>
            )}

            {options.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                    {format(option.label)}
                </option>
            ))}
        </NativeDropdown>
    );

    const selectWithHelperText = helperText ? (
        <Flex.Column gap={Spacing4}>
            {baseSelect}
            <HelperText helperText={helperText} helperTextId={helperTextId} />
        </Flex.Column>
    ) : (
        baseSelect
    );

    if (label) {
        return (
            <LabelContainer>
                <LabelContainer>
                    {label && (
                        <Typography.Label size="S" strong>
                            {label}
                        </Typography.Label>
                    )}
                    {selectWithHelperText}
                </LabelContainer>
            </LabelContainer>
        );
    }

    return selectWithHelperText;
}
